import React from "react";
import { InfoCard } from "./InfoCard";
import type { FormData } from "../../../types/consultation";
import type { ExtractedPatientData } from "../../../types";

// --- Utilitaire pour formater la date ---
const formatDateForDisplay = (dateString: string) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toISOString().split("T")[0];
  } catch {
    return "N/A";
  }
};

// --- Utilitaire pour formater le sexe ---
const getGenderDisplay = (gender: string) => {
  if (!gender) return "N/A";
  const normalizedGender = gender.toLowerCase();
  switch (normalizedGender) {
    case "m":
    case "male":
    case "masculin":
    case "homme":
      return "Masculin";
    case "f":
    case "female":
    case "féminin":
    case "femme":
      return "Féminin";
    default:
      return gender;
  }
};

// --- Utilitaire pour calculer l'âge ---
const calculateAge = (dateString: string): number | null => {
  if (!dateString) return null;
  const birthDate = new Date(dateString);
  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

interface PatientInfoCardProps {
  patientInfo: FormData["patientInfo"];
  patientData?: ExtractedPatientData;
}

export const PatientInfoCard: React.FC<PatientInfoCardProps> = ({
  patientInfo,
  patientData,
}) => {
  // Explicitly type the dateOfBirth as string with non-null assertion
  // Support both dateOfBirth and dateNaissance (some parts of the app use French key)
  const dobString: string =
    (patientData?.dateOfBirth as string) ||
    (patientInfo as any)?.dateOfBirth ||
    (patientInfo as any)?.dateNaissance ||
    "";

  const age =
    patientData?.age !== undefined ? patientData.age : calculateAge(dobString);

  return (
    <InfoCard
      title="Patient"
      icon="👤"
      iconColor="text-blue-500"
      borderColor="border-blue-100"
    >
      <div className="text-gray-700 text-sm space-y-2">
        <div>
          <span className="font-medium">Nom complet : </span>
          {patientData?.fullName ||
            (patientInfo.prenom && patientInfo.nom
              ? `${patientInfo.prenom} ${patientInfo.nom}`.trim()
              : "N/A")}
        </div>
        <div>
          <span className="font-medium">Date de naissance : </span>
          {patientData?.dateOfBirth
            ? formatDateForDisplay(patientData.dateOfBirth as string)
            : formatDateForDisplay(dobString)}
        </div>
        <div>
          <span className="font-medium">Âge : </span>
          {age !== null ? `${age} ans` : "N/A"}
        </div>
        <div>
          <span className="font-medium">Téléphone : </span>
          {patientData?.phone || patientInfo.telephone || "N/A"}
        </div>
        <div>
          <span className="font-medium">Adresse : </span>
          {patientData?.address || patientInfo.adresse || "N/A"}
        </div>
        <div>
          <span className="font-medium">Sexe : </span>
          {patientData?.gender
            ? getGenderDisplay(patientData.gender)
            : getGenderDisplay(patientInfo.sexe)}
        </div>
        <div>
          <span className="font-medium">Email : </span>
          {patientData?.email || patientInfo.email || "N/A"}
        </div>
      </div>
    </InfoCard>
  );
};
